import { useMemo } from 'react';
import { AnalyticsDataPoint, AnalyticsChartData, StatisticsSummary, AnalyticsFilters } from '../types/analytics';
import { 
  transformDataForChart, 
  convertPowerToEnergy, 
  calculateStatistics,
  getAllCalculatedData 
} from '../utils/data-processing';

interface UseMetricsCalculationParams {
  data: AnalyticsDataPoint[];
  filters: AnalyticsFilters;
}

interface UseMetricsCalculationResult {
  chartData: AnalyticsChartData;
  statistics: StatisticsSummary;
  isProcessing: boolean;
}

export function useMetricsCalculation({ 
  data, 
  filters 
}: UseMetricsCalculationParams): UseMetricsCalculationResult {
  
  const result = useMemo(() => {
    if (!data.length) {
      return {
        chartData: { timestamps: [], series: [] },
        statistics: {
          totalEnergyConsumption: 0,
          totalSolarGeneration: 0,
          totalGridImport: 0,
          avgIndoorTempBusinessHours: 0,
          maxOutdoorTempBusinessHours: 0,
          minOutdoorTempBusinessHours: 0
        },
        isProcessing: false
      };
    }

    // Get all calculated data including derived metrics
    const allCalculatedData = getAllCalculatedData(data);
    
    // Process data based on the selected mode
    let processedData = allCalculatedData;
    
    if (filters.mode === 'energy') {
      // Convert power metrics to energy for energy mode
      const powerMetricKeys = ['total_power', 'ac_power', 'grid_import', 'solar_power', 'expected_solar'];
      const powerData = allCalculatedData.filter(point => powerMetricKeys.includes(point.metric));
      const tempData = allCalculatedData.filter(point => !powerMetricKeys.includes(point.metric));
      
      const energyData = convertPowerToEnergy(powerData, filters.timeAggregation);
      processedData = [...energyData, ...tempData];
    }

    // Transform data for chart visualization (only selected metrics will be shown)
    const chartData = transformDataForChart(
      processedData, 
      filters.selectedMetrics,
      filters.dateRange,
      filters.timeAggregation
    );
    
    // Calculate statistics using all available data
    const statistics = calculateStatistics(processedData, filters.mode);

    return {
      chartData,
      statistics,
      isProcessing: false
    };
  }, [data, filters.selectedMetrics, filters.mode, filters.timeAggregation]);

  return result;
}

interface UseComparisonMetricsCalculationParams {
  range1Data: AnalyticsDataPoint[];
  range2Data: AnalyticsDataPoint[];
  filters: AnalyticsFilters;
}

interface UseComparisonMetricsCalculationResult {
  range1ChartData: AnalyticsChartData;
  range2ChartData: AnalyticsChartData;
  range1Statistics: StatisticsSummary;
  range2Statistics: StatisticsSummary;
  isProcessing: boolean;
}

export function useComparisonMetricsCalculation({
  range1Data,
  range2Data,
  filters
}: UseComparisonMetricsCalculationParams): UseComparisonMetricsCalculationResult {
  
  const result = useMemo(() => {
    const processData = (data: AnalyticsDataPoint[], dateRange: { start: string; end: string }) => {
      if (!data.length) {
        return {
          chartData: { timestamps: [], series: [] },
          statistics: {
            totalEnergyConsumption: 0,
            totalSolarGeneration: 0,
            totalGridImport: 0,
            avgIndoorTempBusinessHours: 0,
            maxOutdoorTempBusinessHours: 0,
            minOutdoorTempBusinessHours: 0
          }
        };
      }

      // Get all calculated data including derived metrics
      const allCalculatedData = getAllCalculatedData(data);
      let processedData = allCalculatedData;
      
      if (filters.mode === 'energy') {
        const powerMetricKeys = ['total_power', 'ac_power', 'grid_import', 'solar_power', 'expected_solar'];
        const powerData = allCalculatedData.filter(point => powerMetricKeys.includes(point.metric));
        const tempData = allCalculatedData.filter(point => !powerMetricKeys.includes(point.metric));
        
        const energyData = convertPowerToEnergy(powerData, filters.timeAggregation);
        processedData = [...energyData, ...tempData];
      }

      return {
        chartData: transformDataForChart(
          processedData, 
          filters.selectedMetrics,
          dateRange,
          filters.timeAggregation
        ),
        statistics: calculateStatistics(processedData, filters.mode)
      };
    };

    const range1Result = processData(range1Data, filters.dateRange);
    const range2Result = processData(range2Data, filters.comparison.range2 || filters.dateRange);

    return {
      range1ChartData: range1Result.chartData,
      range2ChartData: range2Result.chartData,
      range1Statistics: range1Result.statistics,
      range2Statistics: range2Result.statistics,
      isProcessing: false
    };
  }, [range1Data, range2Data, filters.selectedMetrics, filters.mode, filters.timeAggregation]);

  return result;
}